import {graphql} from 'gatsby';
import React, {Fragment, memo, useCallback, useState} from 'react';
import UpstreamGallery from 'react-image-lightbox';
import {useIntl} from 'react-intl';

import AddIcon from '../images/add.svg';
import {useDispatch} from '../store';

import {assert} from '../utils';

import style from './image-gallery.module.scss';
import IntlString from './intl-string';
import RatioImage from './ratio-image';

export const query = graphql`
	fragment ImageGallery on Image {
		title {
			...IntlString
		}
		description {
			...IntlString
		}
		copyright {
			...IntlString
		}
		...RatioImage
		local {
			childImageSharp {
				imageGalleryFullImage: fixed(width: 1920, quality: 80) {
					src
				}
			}
		}
	}
`;

/**
 * @param {import("../../tmp/graphql-types").ImageGalleryFragment} e
 * @returns {React.ReactElement | null} ...
 */
export function renderDefaultTitle(e) {
	if (!e.title) {
		return null;
	}
	return (
		<IntlString {...e.title} />
	);
}

/**
 * @param {import("../../tmp/graphql-types").ImageGalleryFragment} e
 * @returns {React.ReactElement | null} ...
 */
export function renderDefaultCaption(e) {
	if (e.description === null && e.copyright === null) {
		return null;
	}

	return (
		<Fragment>
			{e.description && (
				<IntlString {...e.description} />
			)}
			{e.copyright && (
				<Fragment>
					{e.description && (
						<br />
					)}
					(© <IntlString {...e.copyright} />)
				</Fragment>
			)}
		</Fragment>
	);
}

/**
 * @template T
 * @template R
 * @typedef {(entry: T) => R} ImageGalleryMapper
 */

/**
 * @template {import("../../tmp/graphql-types").ImageGalleryFragment} T
 * @param {{
 *   imageStartIndex?: number,
 *   images: Array<T>,
 *   renderTitle?: import('./image-gallery').ImageGalleryMapper<T, React.ReactNode | undefined>,
 *   renderCaption?: import('./image-gallery').ImageGalleryMapper<T, React.ReactNode | undefined>,
 * }} props
 * @returns {React.ReactElement | null} ...
 */
function ImageGallery({
	imageStartIndex = 0,
	images,
	renderTitle = renderDefaultTitle,
	renderCaption = renderDefaultCaption,
}) {
	const intl = useIntl();
	const dispatch = useDispatch();

	const [currIndex, setCurrIndex_] = useState(
		/** @returns {number | null} */
		() => null
	);

	const setCurrIndex = useCallback(
		(newIndex) => {
			dispatch(newIndex !== null ? {type: 'overlayPage'} : {type: 'deOverlayPage'});
			setCurrIndex_(newIndex);
		},
		[dispatch, setCurrIndex_],
	);

	if (images.length === 0) {
		return null;
	}

	assert(imageStartIndex >= 0 && imageStartIndex < images.length);

	return (
		<Fragment>
			<button
				className={style.base}
				onClick={() => setCurrIndex(imageStartIndex)}
			>
				<RatioImage
					className={style.posterImage}
					{...images[imageStartIndex]}
				/>
				<AddIcon className={style.zoomIcon} />
			</button>

			{currIndex !== null && (() => {
				const prevIndex = (currIndex + images.length - 1) % images.length;
				const nextIndex = (currIndex + 1) % images.length;
				const prevImg = images[prevIndex];
				const nextImg = images[nextIndex];
				const currImg = images[currIndex];

				const currImgSrc = currImg.local?.childImageSharp?.imageGalleryFullImage?.src;

				if (currImgSrc === undefined) {
					return null;
				}

				return (
					<UpstreamGallery
						mainSrc={currImgSrc}
						nextSrc={nextImg.local?.childImageSharp?.imageGalleryFullImage?.src}
						prevSrc={prevImg.local?.childImageSharp?.imageGalleryFullImage?.src}
						imageTitle={renderTitle(currImg)}
						imageCaption={renderCaption(currImg)}
						zoomInLabel={intl.formatMessage({id: 'gallery.zoomIn'})}
						zoomOutLabel={intl.formatMessage({id: 'gallery.zoomOut'})}
						closeLabel={intl.formatMessage({id: 'gallery.close'})}
						prevLabel={intl.formatMessage({id: 'gallery.prev'})}
						nextLabel={intl.formatMessage({id: 'gallery.next'})}
						onCloseRequest={() => setCurrIndex(null)}
						onMovePrevRequest={() => setCurrIndex(prevIndex)}
						onMoveNextRequest={() => setCurrIndex(nextIndex)}
						reactModalProps={{
							shouldReturnFocusAfterClose: false,
						}}
						reactModalStyle={{
							overlay: {
								zIndex: 5000,
							},
						}}
						wrapperClassName={style.galleryWrapper}
					/>
				);
			})()}
		</Fragment>
	);
}

export default memo(ImageGallery);
